import { useState, useEffect, useCallback } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { Toast, Loader } from '@vzmi/shp_shared_compo'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label
} from 'recharts'
import ReactResizeDetector from 'react-resize-detector'
import { NoData } from '../../../Common/StatePage'
import { DATE_FORMAT, getFormatData, TIME_DURATION } from '../../../../lib/MomentHelper'
import { dollarFormat, getNumeralFormat } from '../../../../../lib/renderUtil'
import TooltipIcon from '../../../Common/TooltipIcon/TooltipIcon'
import CustomizeXAxisTick from '../../../Dashboard/OperatingDashboard/SaleStat/CustomizeXAxisTick'
import YAxisTick from '../../../Common/NewDashboard/Chart/YAxisTick/YAxisTick'
import CustomizedLegend from '../../../Common/NewDashboard/Chart/Legend/Legend'
import YAxisLabel from '../../../Common/NewDashboard/Chart/YAxisLabel/YAxisLabel'
import LineDot from '../../../Common/NewDashboard/Chart/LineDot/LineDot'
import { useChartSizing } from '../hooks/useChartSizing'
import SellerDailyChartTooltip from './SellerDailyChartTooltip'
import { safeAwait, httpHandler } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import errorHelper from '../../../../../lib/errorHelper'
import { CARRIER } from '../../../Dashboard/OperatingDashboard/constants/carrier'
import { transformPageViewsListToChartDataList } from '../../../Dashboard/OperatingDashboard/Shared'
import { chartDataWithExtraFieldsForSeller } from './helper'
import { mergeSaleData } from '../../../Dashboard/OperatingDashboard/SaleStat/SaleDailyChart'
import styles from './SellerSaleDailyChart.css'

const DEFAULT_TIME_DURATION = TIME_DURATION.DAY.value

export const FIELD = {
  REPORT_DATE: 'reportDate',
  AMOUNT: 'amount',
  QUANTITY: 'quantity'
}

const tooltipContent = (
  <div>
    <div>平均客單 = 訂單成立金額 / 訂單成立數</div>
    <div>轉換率 = 訂購量 / 瀏覽量</div>
  </div>
)

const SellerSaleDailyChart = ({ periodStart, periodEnd, fetchSeSaleStatsByDate, fetchAllListingPageViews }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const {
    barSize,
    tooltipCursorWidth,
    initChart,
    updateBarAndCursorWidth
  } = useChartSizing(data)
  const calcMaxDomain = (minimalSize = 4) => dataMax => {
    const dataSize = _.size(`${dataMax}`)
    const closestData = _.floor(dataMax, (dataSize - 1) * -1)
    const diffData = dataMax - closestData
    const domainMaxSize = closestData + _.ceil(diffData || 1, (dataSize - 1) * -1)

    if (domainMaxSize < minimalSize) {
      return minimalSize
    }
    return domainMaxSize
  }

  const fetchData = useCallback(async ({ periodStart, periodEnd }) => {
    setIsLoading(true)

    const [
      reportDateStart, reportDateEnd
    ] = [moment(periodStart), moment(periodEnd)].map(momentDate => getFormatData(momentDate, DATE_FORMAT.API))
    const fields = ['+newOrder']
    const params = { reportDateStart, reportDateEnd, fields }

    const [saleError, salePayload] = await safeAwait(
      fetchSeSaleStatsByDate(params).then(httpHandler)
    )

    if (saleError) {
      const errorMessage = errorHelper.parseErrorMessage(saleError)
      Toast.error(errorMessage)
      setIsLoading(false)
      return
    }
    const paramsList = [{ reportDateStart, reportDateEnd }]
    const [pageViewError, pageViewList] = await safeAwait(
      Promise.all(_.map(paramsList, fetchAllListingPageViews))
    )

    if (pageViewError) {
      const error = _.get(pageViewError, 'payload.errorData.error[0]')
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      setIsLoading(false)
      return
    }
    const pageViewsList = _.map(pageViewList, resp => _.get(resp, 'data', []))
    const formValues = {
      carriers: [CARRIER.ALL],
      timeDuration: DEFAULT_TIME_DURATION
    }
    const chartDataList = transformPageViewsListToChartDataList(pageViewsList, formValues)
    const chartDataWithExtraFields = chartDataList.map(chartData => chartDataWithExtraFieldsForSeller(chartData))
    const convertedData = _.flow(
      () => _.get(salePayload, 'newOrder.statistics', []),
      statistics => _.map(statistics, stat => ({
        ...stat,
        amount: +stat.amount
      }))
    )()

    const pageViewsData = _.get(chartDataWithExtraFields, '[0].[0].data', [])
    const chartData = mergeSaleData(convertedData, pageViewsData)

    setIsLoading(false)
    setData(chartData)
  }, [fetchSeSaleStatsByDate, fetchAllListingPageViews])

  useEffect(() => {
    if (periodStart && periodEnd) {
      fetchData({ periodStart, periodEnd })
    }
  }, [periodStart, periodEnd, fetchData])

  return (
    <Loader loaded={!isLoading}>
      {_.isEmpty(data)
        ? (
          <NoData iconType='history' />
        )
        : (
          <div className={styles.chartBlock}>
            <TooltipIcon
              className={styles.infoTip}
              tooltipTitle={tooltipContent}
            />
            <ReactResizeDetector
              handleWidth
              handleHeight
              onResize={updateBarAndCursorWidth}
            />
            <ResponsiveContainer
              width='100%'
              height='100%'
              minHeight='250px'
            >
              <ComposedChart
                ref={initChart}
                data={data}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
              >
                <XAxis
                  tickLine={false}
                  dataKey={FIELD.REPORT_DATE}
                  tick={<CustomizeXAxisTick />}
                  interval={1}
                />
                <YAxis
                  yAxisId='left'
                  axisLine={false}
                  tickLine={false}
                  tick={(
                    <YAxisTick
                      prefix='$'
                      customizeTickValue={dollarFormat}
                      textAnchor='end'
                    />
                  )}
                >
                  <Label
                    value='金額'
                    offset={35}
                    position='left'
                    angle={-90}
                    content={<YAxisLabel />}
                  />
                </YAxis>
                <YAxis
                  yAxisId='right'
                  orientation='right'
                  axisLine={false}
                  tickLine={false}
                  domain={[0, calcMaxDomain()]}
                  tick={(
                    <YAxisTick
                      customizeTickValue={getNumeralFormat}
                      textAnchor='start'
                    />
                  )}
                  allowDecimals
                >
                  <Label
                    value='數量'
                    offset={30}
                    position='right'
                    angle={90}
                    content={<YAxisLabel />}
                  />
                </YAxis>
                <CartesianGrid
                  vertical={false}
                  stroke='#f5f5f5'
                />
                <Tooltip
                  cursor={{
                    stroke: '#eee',
                    strokeWidth: tooltipCursorWidth
                  }}
                  position={{ y: -80 }}
                  wrapperStyle={{ zIndex: 1 }}
                  content={<SellerDailyChartTooltip format={DATE_FORMAT.UI} />}
                />
                <Legend
                  align='left'
                  verticalAlign='top'
                  wrapperStyle={{
                    top: '-40px',
                    color: '#9aa0ab'
                  }}
                  content={<CustomizedLegend />}
                />
                <Bar
                  name='訂單成立金額'
                  yAxisId='left'
                  dataKey={FIELD.AMOUNT}
                  legendType='square'
                  fill='#6a96f3'
                  barSize={barSize}
                />
                <Line
                  name='訂單成立數'
                  yAxisId='right'
                  dataKey={FIELD.QUANTITY}
                  stroke='#7dd5a9'
                  strokeWidth='2'
                  activeDot={false}
                  dot={<LineDot />}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
    </Loader>
  )
}
export default SellerSaleDailyChart
